import MainButton, {
  ButtonColor,
} from '@/components/ui/custom/buttons/MainButton';
import { Control, useWatch } from 'react-hook-form';
import {
  CheckCircleIcon as OutlineCheckCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as SolidCheckCircleIcon } from '@heroicons/react/20/solid';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { useRef } from 'react';
import { twJoin } from 'tailwind-merge';
import { extractFormFields } from '@/utils/formUtils';

type ValidateButtonProps<T extends Record<string, any>> = {
  onValidate?: (data: T) => void | Promise<void>;
  control: Control<T>;
  validation?: boolean | null;
  validationFields: (keyof T)[];
  translationPrefix?: string;
  condition?: boolean;
  isValidating?: boolean;
};

type ValidateButtonStyle = {
  icon: React.ReactNode;
  color: ButtonColor;
  text: string;
};

export default function ValidateButton<T extends object>({
  onValidate,
  control,
  validation,
  validationFields,
  condition = true,
  isValidating = false,
}: ValidateButtonProps<T>) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'codeEditorForm',
  });

  const lastValidatedData = useRef<Partial<T> | null>(null);
  const formData = useWatch({ control }) as T;

  const currentValidationData = extractFormFields(formData, validationFields);

  const isSamePayload = isEqual(
    lastValidatedData.current,
    currentValidationData,
  );

  function handleValidateButtonStyle(): ValidateButtonStyle {
    if (validation === null || !isSamePayload) {
      return {
        icon: <OutlineCheckCircleIcon />,
        color: 'primary',
        text: t('validate'),
      };
    }

    if (validation === true) {
      return {
        icon: <SolidCheckCircleIcon />,
        color: 'success',
        text: t('valid'),
      };
    }

    return {
      icon: <ShieldExclamationIcon />,
      color: 'warning',
      text: t('invalid'),
    };
  }

  const validateButtonStyle = handleValidateButtonStyle();

  function handleValidate() {
    lastValidatedData.current = currentValidationData;
    onValidate?.(formData);
  }

  const isValidateMode = validateButtonStyle.color === 'primary';

  return (
    <ConditionalRenderer condition={condition && !!onValidate}>
      <MainButton
        className={twJoin(
          'flex-row-reverse',
          !isValidateMode && 'pointer-events-none',
        )}
        type="button"
        text={validateButtonStyle.text}
        icon={validateButtonStyle.icon}
        onClick={handleValidate}
        loading={isValidating}
        color={validateButtonStyle.color}
      />
    </ConditionalRenderer>
  );
}
